'use client';

import { useState } from 'react';
import type { Coupon } from '@/types/index';
import Image from 'next/image'
import { getCouponLogo } from '@/lib/logos'
import { trustDisplay } from '@/lib/couponRanking'
import type { RankedCoupon } from '@/lib/couponRanking'
import { formatDiscountBadge } from '@/lib/utils'

interface CouponCardProps {
  coupon: Coupon;
  copiedId?: string | null;
  onGetCode?: (coupon: Coupon) => void;
  onCopy?: (coupon: Coupon) => void;
  hideStore?: boolean;
  hideRecentBadge?: boolean; // suppress the "Recently added" banner — used in the Best Coupon module, where the trophy header already conveys "top pick" and a second banner would be a redundant, competing signal
}

function getLogo(coupon: Coupon): string {
  if (coupon.store?.logo) return coupon.store.logo;
  if (coupon.store?.website_url) return getCouponLogo({ store: coupon.store });
  if (coupon.affiliate_url) return getCouponLogo({ affiliate_url: coupon.affiliate_url });
  return '/logo.svg';
}

export default function CouponCard({ coupon, hideStore = false, hideRecentBadge = false }: CouponCardProps) {
  const logo = getLogo(coupon);
  const [loading, setLoading] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  async function handleCTA() {
    if (loading) return;
    setLoading(true);
    const currentPage = window.location.origin + window.location.pathname;
    const popupUrl = `${currentPage}?popup=${encodeURIComponent(coupon.id)}`;
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', coupon.type === 'code' ? 'get_code_click' : 'activate_deal_click', {
        store_name: coupon.store?.name || '',
        coupon_title: coupon.title?.substring(0, 50) || '',
        discount: coupon.discount || '',
        coupon_type: coupon.type,
      })
    }
    window.open(popupUrl, '_blank');
    import('@supabase/supabase-js').then(({ createClient }) => {
      const sb = createClient(process.env.NEXT_PUBLIC_SUPABASE_URL!, process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!)
      sb.from('coupons').select('affiliate_url').eq('id', coupon.id).single()
        .then(({ data }) => { window.location.href = data?.affiliate_url || coupon.affiliate_url })
    }).catch(() => { window.location.href = coupon.affiliate_url })
    import('@supabase/supabase-js').then(({ createClient }) => {
      const sb = createClient(process.env.NEXT_PUBLIC_SUPABASE_URL!, process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!)
      sb.from('coupons').update({ usage_count: (coupon.usage_count || 0) + 1 }).eq('id', coupon.id).then(() => {})
    }).catch(() => {})
  }

  const isCode = coupon.type === 'code';
  const trust = trustDisplay(coupon);
  const rc = coupon as RankedCoupon;
  const recentlyAdded = rc._recentlyAdded === true;
  const isRanked = rc._autoTrending !== undefined || rc._autoFeatured !== undefined || rc._recentlyAdded !== undefined;
  const showTrending = isRanked ? rc._autoTrending === true : coupon.is_trending;
  const showFeatured = isRanked ? rc._autoFeatured === true : coupon.is_featured;
  const fmtDate = (d: Date) => d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });

  return (
    <div
      onClick={handleCTA}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleCTA(); } }}
      className={`bg-white rounded-2xl border shadow-md hover:shadow-lg transition-shadow overflow-hidden h-full flex flex-col cursor-pointer ${recentlyAdded ? 'border-[#EA580C] border-2' : 'border-gray-300'}`}>
      {recentlyAdded && !hideRecentBadge && (
        <div className="bg-orange-50 text-[#C2410C] text-[11px] font-semibold px-3 py-1 text-center border-b border-orange-100">
          ✨ Recently added — likely working
        </div>
      )}
      <div className="flex flex-1">

        <div className="relative flex flex-col items-center justify-center flex-shrink-0 text-center px-2.5 bg-white" style={{ minWidth: '90px', maxWidth: '90px', borderRight: '3px dotted #E5E7EB' }}>
          {hideStore ? (
            <span className="font-extrabold w-full" style={{ fontSize: '22px', lineHeight: '1.05', color: '#EA580C', overflowWrap: 'normal', wordBreak: 'keep-all' }}>
              {formatDiscountBadge(coupon.discount)}
            </span>
          ) : (
            <div className="flex flex-col items-center gap-1 w-full py-2">
              <Image src={logo} alt={coupon.store?.name ?? 'Store'} width={88} height={88} className="w-11 h-11 rounded-lg object-contain" loading="lazy" />
              <span className="text-[13px] font-bold text-gray-800 leading-tight break-words w-full">{coupon.store?.name}</span>
            </div>
          )}
        </div>

        <div className="flex-1 py-2.5 px-4 flex flex-col justify-center gap-1.5 min-w-0">

          {/* HOMEPAGE variant: discount LEFT + trending/featured RIGHT on one line */}
          {!hideStore && (coupon.discount || showTrending || showFeatured) && (
            <div className="flex items-center justify-between gap-2 mb-1">
              {coupon.discount ? (
                <span className="text-[13px] font-bold text-[#EA580C] bg-orange-50 border border-orange-100 px-2 py-0.5 rounded-md whitespace-nowrap flex-shrink-0">{formatDiscountBadge(coupon.discount)}</span>
              ) : <span />}
              {showTrending && (
                <span className="text-[12px] text-orange-800 bg-orange-100 border border-orange-200 px-2 py-px rounded-full font-semibold whitespace-nowrap flex-shrink-0">🔥 Trending</span>
              )}
              {showFeatured && !showTrending && (
                <span className="text-[12px] text-yellow-700 bg-yellow-50 border border-yellow-200 px-2 py-px rounded-full font-semibold whitespace-nowrap flex-shrink-0">⭐ Featured</span>
              )}
            </div>
          )}

          <div className="flex flex-wrap items-start gap-x-2 gap-y-1">
            <p className="text-base md:text-lg font-bold text-gray-900 leading-snug line-clamp-2 basis-full md:basis-0 md:flex-1 min-w-0 order-2 md:order-1">{coupon.title}</p>
            {/* STORE PAGE variant: trending/featured beside title (mobile right / desktop inline) */}
            {hideStore && showTrending && (
              <span className="text-[12px] text-orange-800 bg-orange-100 border border-orange-200 px-2 py-px rounded-full font-semibold whitespace-nowrap w-fit flex-shrink-0 ml-auto md:ml-0 order-1 md:order-2 md:mt-0.5">🔥 Trending</span>
            )}
            {hideStore && showFeatured && !showTrending && (
              <span className="text-[12px] text-yellow-700 bg-yellow-50 border border-yellow-200 px-2 py-px rounded-full font-semibold whitespace-nowrap w-fit flex-shrink-0 ml-auto md:ml-0 order-1 md:order-2 md:mt-0.5">⭐ Featured</span>
            )}
          </div>

          <div className="flex flex-col gap-y-0.5 min-w-0 mt-auto">
            {trust.mode === 'confirmed' && (
              <>
                <span className="text-[12px] font-semibold whitespace-nowrap" style={{ color: '#2f7d5b' }}>👍 {trust.workedCount} confirmed working</span>
                <span className="text-[12px] text-gray-400 whitespace-nowrap">Verified {fmtDate(trust.date)}</span>
              </>
            )}
            {trust.mode === 'verified' && (
              <span className="text-[12px] font-semibold whitespace-nowrap" style={{ color: '#2f7d5b' }}>✓ Verified {fmtDate(trust.date)}</span>
            )}
            {coupon.min_order_value && (
              <span className="text-[11px] text-gray-400 whitespace-nowrap">Min ₹{coupon.min_order_value}</span>
            )}
          </div>

        </div>
      </div>

      {/* Full-width CTA bar — flush with the card's bottom edge, so it picks
          up the card's own rounded-2xl corners via the parent's overflow-hidden.
          Matches the liked mockup's "Shop Deal" style. No real code characters
          are rendered anywhere here — the code only exists once handleCTA()
          actually runs. */}
      <button
        onClick={(e) => { e.stopPropagation(); handleCTA(); }}
        disabled={loading}
        className="w-full flex items-center justify-center gap-2 bg-[#EA580C] hover:bg-[#C2410C] transition-colors disabled:opacity-75 active:scale-[0.99] py-3 px-4"
      >
        <span className="text-[13px] font-bold text-white whitespace-nowrap">
          {loading ? 'Opening...' : isCode ? 'Get Code' : 'Shop Deal'}
        </span>
        <svg width="13" height="13" viewBox="0 0 14 14" fill="none" className="flex-shrink-0"><path d="M3 7h8M7.5 3.5L11 7l-3.5 3.5" stroke="white" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>
      </button>

      <div className="border-t border-gray-200">
        <button onClick={(e) => { e.stopPropagation(); setShowDetails(!showDetails); }} className="w-full flex items-center justify-center gap-1.5 px-4 py-1.5 text-xs text-gray-500 hover:bg-gray-50 transition-colors">
          <svg className={`w-3.5 h-3.5 transition-transform duration-250 ${showDetails ? 'rotate-180' : ''}`} viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M3 5L7 9L11 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span>{showDetails ? 'Hide details' : 'Show details'}</span>
          <span className="w-1 h-1 rounded-full bg-gray-300 mx-0.5" />
          <span className="text-gray-500">Offer info &amp; terms</span>
        </button>

        <div className={`overflow-hidden transition-all duration-300 ease-in-out ${showDetails ? 'max-h-[400px]' : 'max-h-0'}`}>
          <div className="bg-gray-50 border-t border-gray-200 p-4">
            {coupon.description && (
              <p className="text-[13px] text-gray-600 leading-relaxed mb-4">{coupon.description}</p>
            )}
            <div className="grid grid-cols-2 gap-x-4 gap-y-3 mb-4">
              {coupon.discount && (
                <div>
                  <p className="text-[12px] text-gray-500 uppercase tracking-wider mb-0.5">{isCode ? 'Discount' : 'Deal type'}</p>
                  <p className="text-xs font-semibold text-[#9A3412]">{formatDiscountBadge(coupon.discount)}</p>
                </div>
              )}
              {coupon.expiry_date && (
                <div>
                  <p className="text-[12px] text-gray-500 uppercase tracking-wider mb-0.5">Expiry date</p>
                  <p className="text-xs font-semibold text-gray-800">{new Date(coupon.expiry_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                </div>
              )}
              {coupon.min_order_value && (
                <div>
                  <p className="text-[12px] text-gray-500 uppercase tracking-wider mb-0.5">Min. order</p>
                  <p className="text-xs font-semibold text-gray-800">{coupon.min_order_value}</p>
                </div>
              )}
              <div>
                <p className="text-[12px] text-gray-500 uppercase tracking-wider mb-0.5">Status</p>
                <p className="text-xs font-semibold text-green-600">✓ Active &amp; verified</p>
              </div>
            </div>
            {coupon.terms_conditions && (
              <div className="bg-white border border-gray-300 rounded-xl p-3">
                <p className="text-[12px] text-gray-500 uppercase tracking-wider font-medium mb-2">Terms &amp; conditions</p>
                <p className="text-xs text-gray-500 leading-relaxed">{coupon.terms_conditions}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
